// Turning a Custom HTML/CSS block into a picture for the PowerPoint and PDF
// exports.
//
// Those two formats have no stylesheet. Left to the generic path a hand-written
// layout arrived as a column of unstyled sentences: the grid, the panels, the
// colours, the type scale — everything the author actually built — gone, with
// only the words surviving. That is a poor answer for a block whose entire
// purpose is that it looks a particular way.
//
// So the layout is rendered the way a browser renders it and handed to the
// exporters as a single full-bleed image. The technique is SVG's <foreignObject>
// wrapping the markup, loaded through an <img> and painted onto a <canvas>:
// entirely built-ins, no rasterizer dependency, and it reproduces the real CSS
// because it IS the browser's CSS engine doing the work.
//
// It fails closed. Anything that goes wrong — no DOM, a tainted canvas, an
// image that will not decode — returns null, and the caller falls back to the
// old text extraction. A degraded slide beats a broken export.
//
// One rule this path does not share with the HTML export: an SVG loaded through
// an <img> is a sealed document. It may not fetch anything — no remote picture,
// no webfont, no stylesheet. So a layout that links a picture, which is exactly
// what the HTML export should keep as a link, has to have that picture's bytes
// folded in before the SVG is built, or it renders as a hole. The inlining here
// is transient: it exists for the length of one raster and never reaches the
// deck or the exported HTML.

import { EDITOR_RESET_CSS, VIDEO_BASE_CSS, prepareCustomHtmlBlock } from './customCss';
import { describeReadFailure, fetchRemoteImage } from './remoteImage';
import { toFetchableUrl } from './assetUrls';
import { sanitizeSlideLayoutHtml } from './sanitizeHtml';
import { EXPORT_SLIDE_W, EXPORT_CONTENT_H } from './slideFrames';

const CSS_PX_PER_IN = 96;

/**
 * Render at 2x the content region's CSS size. The picture is placed at roughly
 * 13.3in wide, so 1x would be visibly soft on a projector and at print
 * resolution; 2x is the usual retina trade between sharpness and file size.
 */
const RENDER_SCALE = 2;

/**
 * The CSS size of a slide's content region — the box the layout is *laid out*
 * in, which is a different thing from the size it is rasterized at.
 *
 * Laying out at the CSS size and letting the SVG viewBox do the 2x is what
 * keeps the export honest. Laying out directly at 2x would silently halve
 * every fixed-px measurement relative to the slide: `padding: 32px` would come
 * out looking like 16px next to the same layout in the app. Sizes in `cqw`
 * (what the block's brief recommends) are immune either way, but authors do
 * reach for px, and they should get what they wrote.
 */
export const LAYOUT_WIDTH = Math.round(EXPORT_SLIDE_W * CSS_PX_PER_IN);
export const LAYOUT_HEIGHT = Math.round(EXPORT_CONTENT_H * CSS_PX_PER_IN);

export const RENDER_WIDTH = LAYOUT_WIDTH * RENDER_SCALE;
export const RENDER_HEIGHT = LAYOUT_HEIGHT * RENDER_SCALE;
export const RENDER_ASPECT = RENDER_WIDTH / RENDER_HEIGHT;

/** The typography a slide inherits from the app before any custom rule runs. */
const BASE_STYLE = [
  'width: 100%',
  'height: 100%',
  "font-family: ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
  'font-size: 16px',
  'line-height: 1.5',
  'color: #1f2937',
  '-webkit-font-smoothing: antialiased',
].join('; ');

/** XML text, not HTML text: a bare & or < ends the document early. */
const escapeXmlText = (value = '') =>
  String(value).replace(/&/g, '&amp;').replace(/</g, '&lt;');

const escapeXmlAttr = (value = '') =>
  String(value).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');

/**
 * Re-serialize sanitized markup as XHTML.
 *
 * <foreignObject> content is parsed as XML, so an unclosed <img> or <br> — both
 * legal HTML — would abort the whole render. Parsing with the HTML parser and
 * serializing with XMLSerializer converts them.
 */
/**
 * Drop pictures the renderer is never going to fetch.
 *
 * An SVG loaded through an <img> is a closed document: the browser resolves no
 * external subresource inside it, by design. A remote `<img src="https://…">`
 * therefore does not render slowly or partially — it renders as nothing, and
 * the layout exports with a hole where the picture was. That bites video
 * posters in particular, since a thumbnail hosted by YouTube can only be
 * embedded as a data URL if the fetch for it was allowed to succeed.
 *
 * So a remote picture is removed and, if it was a clip's poster, its link
 * falls back to the text card the no-poster path already styles. A labelled
 * card beats an empty rectangle.
 */
const dropRemoteImages = (doc) => {
  doc.body.querySelectorAll('img[src]').forEach((img) => {
    if (/^data:/i.test(img.getAttribute('src') || '')) return;
    const link = img.closest('a.lp-video');
    img.remove();
    if (!link) return;
    link.setAttribute('class', `${link.getAttribute('class') || ''} lp-video--text`.trim());
    if (!link.textContent.trim()) {
      link.textContent = img.getAttribute('alt') || link.getAttribute('href') || '';
    }
  });
};

const toXhtml = (html) => {
  if (typeof DOMParser === 'undefined' || typeof XMLSerializer === 'undefined') return null;
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');
  dropRemoteImages(doc);
  const holder = doc.createElementNS('http://www.w3.org/1999/xhtml', 'div');
  while (doc.body.firstChild) holder.appendChild(doc.body.firstChild);
  return new XMLSerializer().serializeToString(holder)
    // The wrapper itself is not wanted, only its contents.
    .replace(/^<div[^>]*>/, '')
    .replace(/<\/div>$/, '');
};

/**
 * The slide's own background, as CSS, for the formats that cannot carry
 * transparency.
 *
 * PowerPoint takes the PNG's alpha channel, so the raster is left transparent
 * and the slide's background shows through — which also avoids a seam where the
 * content region meets the header. The PDF writer here has no alpha: every
 * image is flattened onto white on its way to JPEG, so an unpainted layout
 * would punch a white rectangle through the slide's background. Painting the
 * background into the raster is what keeps the two formats looking the same.
 */
export const backgroundCss = (background) => {
  if (!background || typeof background !== 'object') return '';
  const parts = [];
  if (background.color) parts.push(`background-color: ${background.color}`);
  if (background.image) {
    parts.push(`background-image: url("${String(background.image).replace(/["\\]/g, '')}")`);
    parts.push('background-size: cover', 'background-position: center', 'background-repeat: no-repeat');
  }
  return parts.join('; ');
};

// --- folding linked pictures into the raster --------------------------------

const REMOTE_URL = /https?:\/\/[^\s"'()<>\\]+/gi;

/**
 * The places a remote address in this markup is actually a *picture*.
 *
 * Scanning for every `https://` in the layout was too eager, and the cost was
 * a lie: a Custom HTML card that links a clip carries
 * `<a href="https://www.youtube.com/watch?v=…">`, which was fetched as if it
 * were an image, came back as `text/html` (or was refused outright), and was
 * reported as "PowerPoint and PDF cannot show <that link> — allow this site in
 * its CORS rules." Nothing was wrong: the address was never a picture, and the
 * card exported perfectly. A warning that fires on healthy decks is how a real
 * warning gets ignored.
 *
 * Three shapes, and `href` is deliberately not among them:
 *   - `src="…"` on an <img> (or any element that takes one)
 *   - a `srcset` candidate
 *   - `url(…)` in CSS, quoted or not
 */
const IMAGE_REFERENCE = /\bsrc\s*=\s*["']?(https?:\/\/[^\s"'>]+)|\bsrcset\s*=\s*["']([^"']+)["']|url\(\s*["']?(https?:\/\/[^\s"')]+)/gi;

/**
 * Every remote picture address one source string references.
 *
 * A bare string that is itself an address (a slide background) counts as one:
 * it has no markup to look inside.
 */
const imageAddressesIn = (text) => {
  const value = String(text || '');
  const found = new Set();
  if (/^https?:\/\/\S+$/i.test(value.trim())) {
    found.add(value.trim());
    return found;
  }
  let match = IMAGE_REFERENCE.exec(value);
  while (match) {
    const [, src, srcset, cssUrl] = match;
    if (src) found.add(src);
    if (cssUrl) found.add(cssUrl);
    if (srcset) {
      // "url 1x, url 2x" — the descriptor is not part of the address.
      srcset.split(',').forEach((candidate) => {
        const address = candidate.trim().split(/\s+/)[0];
        if (/^https?:\/\//i.test(address)) found.add(address);
      });
    }
    match = IMAGE_REFERENCE.exec(value);
  }
  IMAGE_REFERENCE.lastIndex = 0;
  return found;
};

// One fetch per address for the life of the tab. A layout that repeats a
// picture, and a deck that repeats a layout, then cost one download between
// them. The promise is what is kept, not the result, so blocks rasterizing side
// by side — which is how a deck exports — share the one request instead of each
// starting their own. A read that fails is dropped from here, so a picture that
// was briefly unavailable can be fetched again by the next export rather than
// being missing for as long as the tab is open.
const inlinedRemoteImages = new Map();

/**
 * Fetch every remote picture a layout references, as data URLs.
 *
 * Read once, then once more skipping the browser's cache. The second attempt is
 * not superstition — it is the fix for the two ways a picture that is plainly
 * there goes missing from an export:
 *
 * - **A cached 404.** The asset host serves "not found" with `max-age=14400`,
 *   so an address requested once before its file finished uploading stays
 *   missing for four hours. The editor shows the picture perfectly by then,
 *   because an `<img>` and a `fetch` do not share a cache entry — and the
 *   export, reading the stale one, quietly dropped it.
 * - **A response cached without CORS headers**, which the same asset host
 *   returns to any request that carries no `Origin`.
 *
 * Both look identical from here: the first read fails and a read that skips the
 * cache succeeds. A failure is still not fatal and not silent — the address is
 * left as it was, the layout renders without that picture rather than not at
 * all, and the reason is logged.
 */
export const collectInlinedImages = async (sources, { fetchImpl } = {}) => {
  const map = new Map();
  if (!fetchImpl && typeof fetch !== 'function') return map;

  const addresses = new Set();
  sources.filter(Boolean).forEach((text) => {
    imageAddressesIn(text).forEach((url) => addresses.add(url));
  });

  await Promise.all(Array.from(addresses).map(async (url) => {
    if (!inlinedRemoteImages.has(url)) {
      // Same routing as every other export read, so an app-owned asset goes
      // through the dev proxy here as well and the two paths cannot disagree
      // about whether an address is reachable.
      inlinedRemoteImages.set(url, fetchRemoteImage(url, { fetchImpl, requestUrl: toFetchableUrl(url) }));
    }
    try {
      map.set(url, await inlinedRemoteImages.get(url));
    } catch (error) {
      inlinedRemoteImages.delete(url);
      console.warn(
        `[customHtmlRaster] PowerPoint and PDF cannot show ${url} — ${error.message}. `
        + 'The layout will render without it. '
        + describeReadFailure(error)
      );
    }
  }));

  return map;
};

const applyInlinedImages = (text, map) => {
  if (!text || !map || !map.size) return text;
  return String(text).replace(REMOTE_URL, (url) => map.get(url) || url);
};

// --- the box a layout is laid out in ----------------------------------------

/**
 * The box one block is laid out in, in CSS pixels.
 *
 * On a Designer slide the block's frame **is** its box: the app renders the
 * layout inside that frame, so `height: 100%`, a percentage and `1cqw` all mean
 * the frame — not the slide. Rasterizing every block at the slide's full
 * content region instead handed the layout a differently-shaped box than the
 * author designed in, and since the brief tells them to put `overflow: hidden`
 * on their root, whatever no longer fitted was cut off. A card that filled a
 * short, wide frame on screen came out re-flowed and clipped in PowerPoint.
 *
 * A frame dragged past the slide's edge keeps its real size for the same
 * reason: the app lays the layout out at that size and the slide edge does the
 * clipping, so the picture has to be made the same way to agree with it.
 */
export const layoutBoxFor = (frame) => {
  const w = Number(frame?.w);
  const h = Number(frame?.h);
  if (!(w > 0) || !(h > 0)) return { width: LAYOUT_WIDTH, height: LAYOUT_HEIGHT };
  return {
    width: Math.max(1, Math.round((LAYOUT_WIDTH * w) / 100)),
    height: Math.max(1, Math.round((LAYOUT_HEIGHT * h) / 100))
  };
};

/**
 * The layout box for one render.
 *
 * With no explicit box this is the old behaviour exactly — the content region's
 * width, and whatever height the requested pixel proportions imply.
 */
const resolveLayoutBox = (layout, width, height) => {
  const w = Math.round(Number(layout?.width) || LAYOUT_WIDTH);
  const h = Math.round(
    Number(layout?.height) || Math.round((w * height) / width) || LAYOUT_HEIGHT
  );
  return { width: Math.max(1, w), height: Math.max(1, h) };
};

/**
 * Everything one block contributes to a render: its markup as XHTML, and the
 * stylesheet that goes with it.
 *
 * Shared by the SVG and by the pass that measures where the layout's links
 * landed, so the two cannot disagree about what was drawn.
 */
const composeLayout = (block, inlinedImages) => {
  const prepared = prepareCustomHtmlBlock(block);
  const html = applyInlinedImages(prepared.html, inlinedImages);
  const css = applyInlinedImages(prepared.css, inlinedImages);
  const imageVarsCss = applyInlinedImages(prepared.imageVarsCss, inlinedImages);
  const safeHtml = sanitizeSlideLayoutHtml(html);
  if (!safeHtml.trim()) return null;

  const body = toXhtml(safeHtml);
  if (body === null) return null;

  return {
    body,
    // Deck-scoped rules are global in the app; here the block is the whole
    // document, so they apply either way.
    sheet: [EDITOR_RESET_CSS, VIDEO_BASE_CSS, imageVarsCss, css].filter(Boolean).join('\n'),
    deckScoped: prepared.deckScoped,
    blockId: block?.id || 'block'
  };
};

/** Every string a block's pictures could be addressed from. */
const collectableSources = (block, background) => {
  const prepared = prepareCustomHtmlBlock(block);
  return [prepared.html, prepared.css, prepared.imageVarsCss, background?.image];
};

/** Compose the SVG document that carries one block's layout. */
export const buildCustomHtmlSvg = (block, {
  width = RENDER_WIDTH,
  height = RENDER_HEIGHT,
  layout = null,
  background = null,
  inlinedImages = null
} = {}) => {
  const composed = composeLayout(block, inlinedImages);
  if (!composed) return null;
  const { body, sheet, deckScoped } = composed;
  const blockId = escapeXmlAttr(composed.blockId);

  // container-type on the wrapper is what makes cqw units resolve — the block's
  // brief tells authors to size everything in cqw, so without it an exported
  // layout would collapse to unstyled defaults.
  const rootStyle = [
    BASE_STYLE,
    applyInlinedImages(backgroundCss(background), inlinedImages)
  ].filter(Boolean).join('; ');

  // The document is emitted at the layout box's own size, and NEVER at the
  // resolution it will be rasterized to.
  //
  // Inside an <img>, a viewport unit resolves against the SVG's pixel size, not
  // its viewBox: measured in Chrome, an SVG written `width="2560"` with
  // `viewBox="0 0 1280 638"` gives `1vw = 25.6px` where the same markup in a
  // 1280px-wide document gives 12.8px. Every `vw` and `vh` an author wrote was
  // therefore doubled in the export — enough to push clamp()ed type and padding
  // to their maximums, re-flow the layout and push the overflow off the edge of
  // a card that fitted perfectly well on screen.
  //
  // Resolution is added at draw time instead (see rasterizeCustomHtmlBlock),
  // which costs nothing: the renderer re-rasterizes the vector at the size it
  // is drawn, pixel for pixel identical to a natively oversized document.
  const { width: boxW, height: boxH } = resolveLayoutBox(layout, width, height);

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${boxW}" height="${boxH}" viewBox="0 0 ${boxW} ${boxH}">`
    + `<foreignObject x="0" y="0" width="${boxW}" height="${boxH}">`
    + `<div xmlns="http://www.w3.org/1999/xhtml" style="${escapeXmlAttr(rootStyle)}">`
    + `<style>${escapeXmlText(sheet)}</style>`
    + `<div class="slide-custom-html" data-block="${blockId}" data-css-scope="${deckScoped ? 'deck' : 'slide'}"`
    + ` style="width: 100%; height: 100%; container-type: inline-size;">${body}</div>`
    + '</div></foreignObject></svg>';
};

// --- keeping the layout's links clickable -----------------------------------

/**
 * Where each of a layout's links landed, as fractions of the rendered picture.
 *
 * A raster is one flat image, so an `<a>` inside it stops being a link the
 * moment it is drawn — a card whose whole point is "watch this on YouTube"
 * exported as a picture of a button. PowerPoint and PDF both let a clickable
 * region be laid over a picture, so the geometry is measured here and the
 * exporters put a hotspot on top of each one.
 *
 * The measuring is done in an off-screen iframe sized to the layout box, which
 * is the only environment that agrees with the picture on all three counts:
 * its viewport is the box, so `vw`, `vh` and `@media` resolve exactly as they
 * do inside the SVG; and it is a document of its own, so the unscoped CSS reset
 * the sheet carries (`*`, `img`, `ul`) cannot reach the app around it. Measured
 * in a shadow root instead, every `vw` resolved against the browser window and
 * the geometry drifted with the size of it.
 */
/** How long the measuring pass waits for a layout's pictures. */
export const IMAGE_SETTLE_TIMEOUT_MS = 3000;

/**
 * Wait for a layout's pictures to have a size, and never wait forever.
 *
 * This used to be `img.decode()`, and `decode()` is a promise that can simply
 * never settle. The measuring iframe is parked at `left: -20000px`, and a
 * document the browser never paints is one Chromium may never rasterize — so
 * `decode()` on an image that is already `complete`, with a real
 * `naturalWidth`, hangs. Nothing downstream had a deadline either, so the
 * whole export stopped: no file, no error, and a "Preparing PowerPoint…"
 * toast that stayed up indefinitely.
 *
 * Two changes close it. `decode()` is not used at all — it promises "decoded
 * and ready to paint", which is more than measuring geometry needs, and it is
 * the part that hangs. And the wait has a deadline, on the same reasoning the
 * rasterizer's own `loadImage` already carries: a layout measured slightly
 * early costs a link hotspot's position, where a layout that never finishes
 * costs the entire deck.
 */
const waitForImages = (images, timeoutMs = IMAGE_SETTLE_TIMEOUT_MS) => {
  const pending = images.filter((img) => !(img.complete && img.naturalWidth > 0));
  if (!pending.length) return Promise.resolve();
  return Promise.race([
    Promise.all(pending.map((img) => new Promise((resolve) => {
      img.addEventListener('load', resolve, { once: true });
      img.addEventListener('error', resolve, { once: true });
    }))),
    new Promise((resolve) => { setTimeout(resolve, timeoutMs); })
  ]);
};

export const measureCustomHtmlLinks = async (block, { layout = null, inlinedImages = null } = {}) => {
  if (typeof document === 'undefined' || !document.body) return [];
  // Composing is inside the guard too: a layout with no measurable links is
  // the same outcome as a block that cannot be composed at all — a picture —
  // and neither is worth failing an export over.
  let composed = null;
  try {
    composed = composeLayout(block, inlinedImages);
  } catch (error) {
    console.warn('[customHtmlRaster] Could not compose a layout for measuring', error);
    return [];
  }
  if (!composed) return [];

  const box = resolveLayoutBox(layout, RENDER_WIDTH, RENDER_HEIGHT);
  const frame = document.createElement('iframe');
  frame.setAttribute('aria-hidden', 'true');
  frame.setAttribute('tabindex', '-1');
  frame.style.cssText = 'position: fixed; left: -20000px; top: 0; z-index: -1; border: 0; '
    + `pointer-events: none; width: ${box.width}px; height: ${box.height}px;`;

  try {
    document.body.appendChild(frame);
    const doc = frame.contentDocument;
    if (!doc) return [];
    doc.open();
    doc.write(
      '<!doctype html><html><head><meta charset="utf-8">'
      + '<style>html,body{margin:0;padding:0;width:100%;height:100%;overflow:hidden}</style>'
      // A closing tag inside the author's CSS would end the <style> early.
      + `<style>${composed.sheet.replace(/<\//g, '<\\/')}</style></head><body>`
      + `<div style="${BASE_STYLE}">`
      + `<div class="slide-custom-html" data-block="${composed.blockId}"`
      + ` data-css-scope="${composed.deckScoped ? 'deck' : 'slide'}"`
      + ' style="width: 100%; height: 100%; container-type: inline-size;">'
      + `${composed.body}</div></div></body></html>`
    );
    doc.close();

    const wrapper = doc.querySelector('.slide-custom-html');
    if (!wrapper) return [];

    // A picture that has not loaded yet has no intrinsic size, and a layout
    // that leans on one would be measured mid-reflow.
    await waitForImages(Array.from(wrapper.querySelectorAll('img')));

    const base = wrapper.getBoundingClientRect();
    if (!base.width || !base.height) return [];

    const links = [];
    wrapper.querySelectorAll('a[href]').forEach((anchor) => {
      const href = String(anchor.getAttribute('href') || '').trim();
      // Only addresses that mean something once the deck has left the app.
      if (!/^(https?:|mailto:)/i.test(href)) return;
      const rect = anchor.getBoundingClientRect();
      if (rect.width < 1 || rect.height < 1) return;

      // Clipped to the picture: a link the author's `overflow: hidden` cut off
      // is not clickable where it was never drawn.
      const left = Math.max(0, (rect.left - base.left) / base.width);
      const top = Math.max(0, (rect.top - base.top) / base.height);
      const right = Math.min(1, (rect.right - base.left) / base.width);
      const bottom = Math.min(1, (rect.bottom - base.top) / base.height);
      if (right <= left || bottom <= top) return;

      links.push({ href, x: left, y: top, w: right - left, h: bottom - top });
    });
    return links;
  } catch (error) {
    // A layout with no measurable links is the same outcome as a layout with
    // none: a picture. Never a reason to lose the picture.
    console.warn('[customHtmlRaster] Could not measure a layout\'s links', error);
    return [];
  } finally {
    frame.remove();
  }
};

/**
 * How long to wait for one layout to decode before giving up on it.
 *
 * The timeout is not belt-and-braces: an <img> pointed at an SVG the engine
 * will not render can fire neither `load` nor `error` — it simply never
 * settles. Without a deadline that hangs the whole export on one bad block,
 * which is far worse than the slide falling back to its text.
 */
const DECODE_TIMEOUT_MS = 15000;

const loadImage = (src, timeoutMs = DECODE_TIMEOUT_MS) => new Promise((resolve, reject) => {
  if (typeof document === 'undefined') { reject(new Error('no document')); return; }
  const img = document.createElement('img');
  let settled = false;
  const finish = (fn, value) => {
    if (settled) return;
    settled = true;
    clearTimeout(timer);
    img.onload = null;
    img.onerror = null;
    fn(value);
  };
  const timer = setTimeout(() => finish(reject, new Error('svg render timed out')), timeoutMs);
  img.onload = () => finish(resolve, img);
  img.onerror = () => finish(reject, new Error('svg failed to decode'));
  img.src = src;
});

/**
 * Rasterize one block. Returns a PNG data URL, or null when the layout is empty
 * or anything in the pipeline refuses.
 */
export const rasterizeCustomHtmlBlock = async (block, options = {}) => {
  const box = resolveLayoutBox(options.layout, RENDER_WIDTH, RENDER_HEIGHT);
  const width = options.width || box.width * RENDER_SCALE;
  const height = options.height || box.height * RENDER_SCALE;

  try {
    // The SVG cannot fetch, so every picture it references has to be carried
    // inside it. This runs before the document is composed, on a copy.
    const inlinedImages = options.inlinedImages || await collectInlinedImages(
      collectableSources(block, options.background),
      { fetchImpl: options.fetchImpl }
    );

    const svg = buildCustomHtmlSvg(block, {
      width,
      height,
      layout: box,
      background: options.background,
      inlinedImages
    });
    if (!svg) return null;
    if (typeof document === 'undefined') return null;

    const url = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
    const img = await loadImage(url, options.timeoutMs);

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    // Left transparent: the slide's own background colour or image is painted
    // by the exporter underneath, exactly as the app composites them.
    ctx.drawImage(img, 0, 0, width, height);

    const dataUrl = canvas.toDataURL('image/png');
    return dataUrl && dataUrl.startsWith('data:image/png') ? dataUrl : null;
  } catch (error) {
    console.warn('[customHtmlRaster] Falling back to text for a custom layout', error);
    return null;
  }
};

const isCustomHtmlBlock = (block) => block && block.type === 'custom-html';

/**
 * Attach a rendered picture to every custom-html block in a slide list.
 *
 * Each block also gets the proportions it was rendered at and the addresses of
 * the links inside it, because a picture alone is not enough for either
 * exporter: without the aspect they would place it at the content region's
 * shape whatever shape it really is, and without the links a layout's buttons
 * would export as pictures of buttons.
 *
 * Returns a new list; the caller's slides are untouched. Blocks that cannot be
 * rendered are returned as they were, so the exporters fall back per block
 * rather than all-or-nothing.
 */
export const withCustomHtmlRenders = async (slides = [], options = {}) => {
  if (!Array.isArray(slides) || !slides.length) return slides;
  if (!slides.some((slide) => (slide?.blocks || []).some(isCustomHtmlBlock))) return slides;

  // `style` is the exporters' style payload; resolving the background here the
  // same way buildDeckModel does keeps the picture on the colour the slide will
  // actually be.
  const style = options.style || {};
  const resolveBackground = (slideId) =>
    (slideId && style.backgrounds?.[slideId]) || style.defaultBackground || null;
  // Only a Designer slide positions blocks by frame. In the ordinary flow a
  // layout is the slide's body and gets the whole content region, as before.
  const framed = String(style.designMode || '') === 'canvas';

  return Promise.all(slides.map(async (slide) => {
    const blocks = slide?.blocks;
    if (!Array.isArray(blocks) || !blocks.some(isCustomHtmlBlock)) return slide;

    const background = options.opaque ? resolveBackground(slide?.slideId) : null;

    const nextBlocks = await Promise.all(blocks.map(async (block) => {
      if (!isCustomHtmlBlock(block)) return block;
      const layout = framed
        ? layoutBoxFor(block.frame)
        : { width: LAYOUT_WIDTH, height: LAYOUT_HEIGHT };

      // Fetched once and used by both passes, so the picture and the link
      // geometry are measured against exactly the same layout.
      const inlinedImages = await collectInlinedImages(
        collectableSources(block, background),
        { fetchImpl: options.fetchImpl }
      );

      const renderedImage = await rasterizeCustomHtmlBlock(block, {
        ...options, background, layout, inlinedImages
      });
      if (!renderedImage) return block;

      return {
        ...block,
        renderedImage,
        renderedAspect: layout.width / layout.height,
        renderedLinks: await measureCustomHtmlLinks(block, { layout, inlinedImages })
      };
    }));

    return { ...slide, blocks: nextBlocks };
  }));
};
